import { useEffect, useState, type ReactNode } from 'react';
import { FiUsers, FiArrowLeft, FiPaperclip, FiDownload, FiSend, FiCheckCircle, FiClock, FiInbox, FiFileText, FiClipboard, FiCalendar, FiCheck, FiUpload } from 'react-icons/fi';
import { formatarDataBR } from '../../lib/data';
import { Spinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/Button';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { useDadosVivos } from '../../hooks/useDadosVivos';
import {
  participacaoService, type ProcessoParticipo, type ParticipoDetalhe,
} from '../../services/participacao.service';

const STATUS_LABEL: Record<string, string> = {
  em_tramitacao: 'Em tramitação', em_analise: 'Em análise', em_diligencia: 'Em diligência',
  aguardando_assinatura: 'Aguardando assinatura', deferido: 'Deferido', indeferido: 'Indeferido',
  arquivado: 'Arquivado', cancelado: 'Cancelado', concluido: 'Concluído', aberto: 'Aberto',
};

const ACAO_LABEL: Record<string, string> = {
  ver: 'Ver / baixar', responder: 'Responder', anexar: 'Anexar',
  parecer: 'Emitir parecer', assinar: 'Assinar', ciencia: 'Dar ciência',
};

const TAREFA_LABEL: Record<string, string> = {
  ciencia: 'Dar ciência', responder: 'Responder / enviar mensagem', anexar: 'Anexar documento', assinar: 'Assinar documento',
};

export function ProcessosParticipoPage() {
  const { dados, carregando, recarregar } = useDadosVivos<ProcessoParticipo[]>(
    () => participacaoService.meusProcessos(), [],
  );
  const processos = dados ?? [];
  const [aberto, setAberto] = useState<string | null>(null);

  if (aberto) {
    return <Detalhe participanteId={aberto} onVoltar={() => { setAberto(null); recarregar(); }} />;
  }

  return (
    <PortalPage
      titulo="Processos em que participo"
      subtitulo="Documentos que a prefeitura compartilhou com você para análise, resposta ou ciência."
      icone={<FiUsers size={26} />}
      largura="larga"
      voltarHref="/"
    >
      {carregando ? (
        <div className="flex justify-center py-16"><Spinner tamanho="lg" /></div>
      ) : processos.length === 0 ? (
        <SectionCard>
          <div className="text-center py-12">
            <div className="w-14 h-14 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3">
              <FiInbox className="text-slate-400" size={26} />
            </div>
            <p className="text-sm text-slate-700 font-medium">Nenhum processo compartilhado com você.</p>
            <p className="text-xs text-slate-500 mt-1">Quando a prefeitura compartilhar um documento, ele aparece aqui.</p>
          </div>
        </SectionCard>
      ) : (
        <SectionCard semPadding>
          <ul className="divide-y divide-slate-100">
            {processos.map((p) => {
              const convitePendente = p.status_participante === 'convidado';
              const pendente = !convitePendente && ((p.acoes.includes('responder') && !p.respondido_em) || (p.acoes.includes('ciencia') && !p.ciencia_em));
              return (
                <li key={p.participante_id}>
                  <button onClick={() => setAberto(p.participante_id)} className="flex w-full items-start gap-4 p-4 text-left hover:bg-slate-50 transition">
                    <FiUsers className="text-slate-400 flex-shrink-0 mt-0.5" size={18} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-xs text-slate-500 tabular-nums">{p.numero}</span>
                        <span className="text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded border bg-slate-50 text-slate-600 border-slate-200">
                          {STATUS_LABEL[p.status] ?? p.status}
                        </span>
                        {convitePendente && <span className="text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded bg-blue-100 text-blue-800">Convite pendente</span>}
                        {p.status_participante === 'recusado' && <span className="text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-500">Recusado</span>}
                        {pendente && <span className="text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded bg-amber-100 text-amber-800">Ação pendente</span>}
                      </div>
                      <div className="text-sm font-medium text-slate-900 mt-0.5 truncate">{p.titulo}</div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-slate-500">
                        {p.enviado_em && <span>Compartilhado em {formatarDataBR(p.enviado_em)}</span>}
                        {p.prazo_resposta && <span className="flex items-center gap-1"><FiClock size={11} /> Prazo {formatarDataBR(p.prazo_resposta)}</span>}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </SectionCard>
      )}
    </PortalPage>
  );
}

function Detalhe({ participanteId, onVoltar }: { participanteId: string; onVoltar: () => void }) {
  const [det, setDet] = useState<ParticipoDetalhe | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [resposta, setResposta] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [baixando, setBaixando] = useState<string | null>(null);
  const [arquivoAnexo, setArquivoAnexo] = useState<File | null>(null);
  const [anexando, setAnexando] = useState(false);
  const [assinando, setAssinando] = useState(false);

  async function carregar() {
    setCarregando(true);
    try {
      const d = await participacaoService.ver(participanteId);
      setDet(d);
      setResposta(d.resposta_texto ?? '');
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setCarregando(false);
    }
  }
  useEffect(() => { void carregar(); /* eslint-disable-next-line */ }, [participanteId]);

  async function baixar(anexoId: string) {
    setBaixando(anexoId);
    try {
      const url = await participacaoService.anexoUrl(participanteId, anexoId);
      window.open(url, '_blank');
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBaixando(null);
    }
  }

  async function enviarResposta() {
    setEnviando(true);
    try {
      await participacaoService.responder(participanteId, resposta.trim());
      await carregar();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setEnviando(false);
    }
  }

  async function darCiencia() {
    setEnviando(true);
    try {
      await participacaoService.darCiencia(participanteId);
      await carregar();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setEnviando(false);
    }
  }

  async function responderConvite(aceitar: boolean) {
    setEnviando(true);
    try {
      await participacaoService.responderConvite(participanteId, aceitar);
      await carregar();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setEnviando(false);
    }
  }

  async function enviarAnexo() {
    if (!arquivoAnexo) return;
    setAnexando(true);
    try {
      await participacaoService.anexar(participanteId, arquivoAnexo);
      setArquivoAnexo(null);
      await carregar();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setAnexando(false);
    }
  }

  async function assinar(tipo: 'simples' | 'qualificada') {
    setAssinando(true);
    try {
      await participacaoService.assinar(participanteId, tipo);
      await carregar();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setAssinando(false);
    }
  }

  return (
    <PortalPage
      titulo={det?.documento.titulo ?? 'Documento compartilhado'}
      subtitulo={det ? det.documento.numero : undefined}
      icone={<FiUsers size={26} />}
      largura="larga"
      acoes={<Button variant="outline" onClick={onVoltar}><FiArrowLeft size={14} /> Voltar</Button>}
    >
      {carregando ? (
        <div className="flex justify-center py-16"><Spinner tamanho="lg" /></div>
      ) : erro || !det ? (
        <SectionCard><p className="text-sm text-red-600">{erro ?? 'Não foi possível carregar.'}</p></SectionCard>
      ) : (
        <div className="space-y-5">
          {/* Explicação amigável do que é esta tela */}
          <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900">
            A Prefeitura compartilhou este <strong>processo administrativo</strong> com você. Abaixo você vê o que
            está sendo pedido, os dados do processo e os documentos liberados, e pode responder ou dar ciência.
          </div>

          {/* Aceite do convite (Fase 4): primeiro passo do externo */}
          {det.status === 'convidado' && (
            <SectionCard titulo="1. Aceite o convite">
              <p className="text-sm text-slate-600">
                A Prefeitura convidou você a participar deste processo
                {det.acoes.filter((a) => a !== 'ver').length > 0 && <> e poderá: <strong>{det.acoes.filter((a) => a !== 'ver').map((a) => ACAO_LABEL[a] ?? a).join(', ')}</strong></>}.
                Aceite para acessar os documentos.
              </p>
              <div className="mt-3 flex gap-2">
                <Button loading={enviando} onClick={() => responderConvite(true)}><FiCheckCircle size={14} /> Aceitar convite</Button>
                <Button variant="outline" loading={enviando} onClick={() => responderConvite(false)}>Recusar</Button>
              </div>
            </SectionCard>
          )}
          {det.status === 'recusado' && (
            <SectionCard><p className="text-sm text-slate-600">Você recusou o convite para este processo.</p></SectionCard>
          )}

          {/* O que a prefeitura está solicitando (foco da tela) */}
          {(() => {
            const tarefas = det.acoes.filter((a) => a !== 'ver');
            const feito: Record<string, boolean> = { ciencia: !!det.ciencia_em, responder: !!det.respondido_em, anexar: det.tem_anexo_proprio, assinar: !!det.assinado_em };
            if (tarefas.length === 0 && !det.solicitacao_texto && !det.prazo_resposta) return null;
            return (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
                  <FiClipboard size={16} /> O que a Prefeitura solicita
                </div>
                {det.solicitacao_texto && <p className="mt-2 text-sm text-amber-900 whitespace-pre-line">{det.solicitacao_texto}</p>}
                {det.prazo_resposta && (
                  <p className="mt-2 flex items-center gap-1.5 text-sm font-medium text-amber-800">
                    <FiClock size={14} /> Prazo: {formatarDataBR(det.prazo_resposta)}
                  </p>
                )}
                {tarefas.length > 0 && (
                  <ul className="mt-3 space-y-1.5">
                    {tarefas.map((t) => (
                      <li key={t} className="flex items-center gap-2 text-sm">
                        <span className={`flex h-5 w-5 items-center justify-center rounded-full ${feito[t] ? 'bg-emerald-500 text-white' : 'border border-amber-400 text-transparent'}`}>
                          <FiCheck size={12} />
                        </span>
                        <span className={feito[t] ? 'text-slate-500 line-through' : 'text-amber-900 font-medium'}>{TAREFA_LABEL[t] ?? t}</span>
                        {feito[t] && <span className="text-[11px] text-emerald-600">concluído</span>}
                      </li>
                    ))}
                  </ul>
                )}
                {det.status === 'convidado' && <p className="mt-2 text-xs text-amber-700">Aceite o convite acima para executar as tarefas.</p>}
              </div>
            );
          })()}

          {/* Dados do processo */}
          <SectionCard titulo="Sobre o processo" icone={<FiFileText size={18} />}>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
              <Info rotulo="Número" valor={det.documento.numero} />
              <Info rotulo="Situação" valor={STATUS_LABEL[det.documento.status] ?? det.documento.status} />
              <Info rotulo="Assunto" valor={det.documento.titulo} />
              <Info rotulo="Aberto em" valor={formatarDataBR(det.documento.data_abertura)} icone={<FiCalendar size={12} />} />
            </dl>
            <div className="mt-3 border-t border-slate-100 pt-3">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Do que se trata</div>
              {det.documento.descricao ? (
                <div className="prose prose-sm max-w-none text-slate-700" dangerouslySetInnerHTML={{ __html: det.documento.descricao }} />
              ) : (
                <p className="text-sm text-slate-400">A Prefeitura não compartilhou o teor do processo — consulte os documentos abaixo.</p>
              )}
            </div>
          </SectionCard>

          <SectionCard titulo={`Documentos compartilhados (${det.anexos.length})`} icone={<FiPaperclip size={18} />}>
            {det.anexos.length === 0 ? (
              <p className="text-sm text-slate-500">Nenhum documento foi compartilhado com você ainda.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {det.anexos.map((a) => (
                  <li key={a.id} className="flex items-center justify-between gap-3 py-2.5">
                    <span className="flex min-w-0 items-center gap-2 text-sm text-slate-700">
                      <FiPaperclip size={14} className="text-slate-400 flex-shrink-0" />
                      <span className="truncate">{a.nome_original}</span>
                    </span>
                    <Button variant="outline" className="h-8 px-3 text-xs" loading={baixando === a.id} onClick={() => baixar(a.id)}>
                      <FiDownload size={13} /> Baixar
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>

          {det.status === 'aceito' && det.acoes.includes('ciencia') && (
            <SectionCard titulo="Ciência">
              {det.ciencia_em ? (
                <p className="flex items-center gap-2 text-sm text-emerald-700"><FiCheckCircle size={15} /> Ciência registrada em {formatarDataBR(det.ciencia_em)}.</p>
              ) : (
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm text-slate-600">Confirme que tomou ciência do conteúdo compartilhado.</p>
                  <Button loading={enviando} onClick={darCiencia}><FiCheckCircle size={14} /> Dar ciência</Button>
                </div>
              )}
            </SectionCard>
          )}

          {det.status === 'aceito' && det.acoes.includes('responder') && (
            <SectionCard titulo="Sua resposta">
              {det.respondido_em && (
                <p className="mb-2 text-xs text-emerald-700">Respondido em {formatarDataBR(det.respondido_em)}. Você pode atualizar a resposta.</p>
              )}
              <textarea
                value={resposta}
                onChange={(e) => setResposta(e.target.value)}
                rows={5}
                placeholder="Escreva sua resposta..."
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-institutional"
              />
              <div className="mt-2 flex justify-end">
                <Button loading={enviando} disabled={resposta.trim().length < 2} onClick={enviarResposta}>
                  <FiSend size={14} /> Enviar resposta
                </Button>
              </div>
            </SectionCard>
          )}

          {det.status === 'aceito' && det.acoes.includes('anexar') && (
            <SectionCard titulo="Anexar documento">
              {det.tem_anexo_proprio && (
                <p className="mb-2 flex items-center gap-2 text-sm text-emerald-700"><FiCheckCircle size={15} /> Você já anexou um documento. Pode anexar outros, se precisar.</p>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <input type="file" onChange={(e) => setArquivoAnexo(e.target.files?.[0] ?? null)} className="text-sm" />
                <Button loading={anexando} disabled={!arquivoAnexo} onClick={enviarAnexo}>
                  <FiUpload size={14} /> Enviar documento
                </Button>
              </div>
            </SectionCard>
          )}

          {det.status === 'aceito' && det.acoes.includes('assinar') && (
            <SectionCard titulo="Assinar documento">
              {det.assinado_em ? (
                <p className="flex items-center gap-2 text-sm text-emerald-700"><FiCheckCircle size={15} /> Documento assinado em {formatarDataBR(det.assinado_em)}.</p>
              ) : (
                <>
                  <p className="text-sm text-slate-600">Assine eletronicamente o documento deste processo (Lei 14.063/2020).</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button loading={assinando} onClick={() => assinar('simples')}><FiCheckCircle size={14} /> Assinar (simples)</Button>
                    <Button variant="outline" loading={assinando} onClick={() => assinar('qualificada')}>Assinar com certificado (ICP)</Button>
                  </div>
                  <p className="mt-1 text-[11px] text-slate-400">A assinatura com certificado ICP está em homologação (simulada) até a contratação da certificadora.</p>
                </>
              )}
            </SectionCard>
          )}
        </div>
      )}
    </PortalPage>
  );
}

function Info({ rotulo, valor, icone }: { rotulo: string; valor: string; icone?: ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wider text-slate-400">{rotulo}</dt>
      <dd className="mt-0.5 flex items-center gap-1.5 text-sm text-slate-800">{icone}{valor}</dd>
    </div>
  );
}
